import { Portals } from '@backoffice-frontend/common'
import type * as React from 'react'
import { Divider } from '../../patterns/Divider'
import { RenderToPortal } from '../RenderToPortal'
import type { MoiaTopBarProps } from './MoiaTopBar'
import { MoiaTopBar } from './MoiaTopBar'

export type MoiaHighTopBarProps = MoiaTopBarProps & {
  /**
   * @example
   * ```
   *    <MoiaGrid column>
          <MoiaButton>action</MoiaButton>
          <MoiaButton>action</MoiaButton>
        </MoiaGrid>
   * ```
   */
  actions?: React.ReactNode
  /**
   * @example
   * ```
   * <>
      <MoiaTopBarTab to="/">Tab 1</MoiaTopBarTab>
      <MoiaTopBarTab to="/tab2">Tab 2</MoiaTopBarTab>
      <MoiaTopBarTab to="/tab3">Tab 3</MoiaTopBarTab>
    </>
   * ```
   */
  tabs?: React.ReactNode
  subHeader?: React.ReactNode
  className?: string
}

/**
 * Top Header component that renders into the DedicatedHeader Portal
 * - [figma design specs](https://www.figma.com/file/n3mcyd7G5ZNvzJt7WOKKHD/Library-Backoffice?node-id=4654%3A10311)
 */
export const MoiaHighTopBar = ({
  trailing,
  leading,
  actions,
  tabs,
  children,
  subHeader,
  className,
}: MoiaHighTopBarProps) => {
  return (
    <MoiaTopBar trailing={trailing} leading={leading}>
      <RenderToPortal id={Portals.DedicatedHeaderTabs}>
        {subHeader}
        <Divider
          css={theme => ({
            marginTop: theme.space.Space2,
            marginBottom: theme.space.Space2,

            [theme.breakpoints.down('md')]: {
              marginLeft: '-8px',
              marginRight: '-8px',
              marginTop: theme.space.Space1,
              marginBottom: theme.space.Space1,
            },
          })}
        />
        <div
          className={className}
          css={{
            alignContent: 'center',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div
            css={{
              alignItems: 'center',
              display: 'flex',
              height: '36px',
              whiteSpace: 'nowrap',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {tabs}
          </div>
          <div>{actions}</div>
        </div>
        {children}
      </RenderToPortal>
    </MoiaTopBar>
  )
}
